import React from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Link } from "react-router-dom";
import { CardActions, Button, Grid } from "@material-ui/core";
import { useAuth } from "../services/authentication";
import IProjectHeader from "../models/IProjectHeader";
import { apiPost } from "../services/api";
import { useSnackbar } from "notistack";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import SyncIcon from "@material-ui/icons/Sync";

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        backgroundColor: theme.palette.secondary.light,
        margin: theme.spacing(1),
        marginLeft: theme.spacing(0),
        boxshadow: theme.shadows[3],
        minHeight: "350px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between"
    },
    viewProjectButton: {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,

        padding: theme.spacing(1, 19, 1, 19),
        fontSize: "1rem",
        "&:hover": {
            backgroundColor: theme.palette.primary.dark
        },
        "&:disabled": {
            backgroundColor: "#bdbdbd",
            color: "#f5f5f5"
        }
    },
    contentTop: {
        padding: theme.spacing(4, 4, 0, 4)
    },
    contentMiddle: {
        padding: theme.spacing(0, 4, 0, 4)
    },
    contentBottom: {
        padding: theme.spacing(0, 4, 4, 4)
    },
    title: {
        fontSize: 14
    },
    pos: {
        marginBottom: 12
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(4),
        top: theme.spacing(4)
    },
    button: {
        maxWidth: "30px",
        margin: theme.spacing(0)
    }
}));

interface IUserProject {
    projectHeader: IProjectHeader;
    enableAddUserFunction: boolean;
}

const UserProject: React.FC<IUserProject> = ({
    projectHeader,
    enableAddUserFunction
}) => {
    const statusValues = [
        "Not Started",
        "Project Design",
        "Awaiting Tracer Injection",
        "Awaiting Samples",
        "Analysing Samples",
        "Reporting",
        "Complete"
    ];

    const projectTypes = [
        "Not Selected",
        "Frac",
        "Inflow",
        "Interwell",
        "Mud Tagging",
        "Perf Tagging"
    ];

    const [syncing, setSyncing] = React.useState(false);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const handleSync = async () => {
        setSyncing(true);

        const snackbarKey = enqueueSnackbar(
            `Syncing ${projectHeader.name}, please wait`,
            {
                variant: "info"
            }
        );

        try {
            await apiPost(`projects/all/${projectHeader.id}/syncs`);

            enqueueSnackbar(`Complete syncing ${projectHeader.name}`, {
                variant: "success"
            });
        } catch (e) {
            enqueueSnackbar(
                `Error syncing ${projectHeader.name}, please try again`,
                {
                    variant: "error"
                }
            );
        } finally {
            setSyncing(false);

            if (snackbarKey != null) {
                closeSnackbar(snackbarKey);
            }
        }
    };

    const { authState } = useAuth();
    const classes = useStyles();

    return (
        <Card className={classes.card}>
            <CardContent className={classes.contentTop}>
                <Typography variant="h5" component="h2">
                    {projectHeader.name}
                </Typography>
            </CardContent>

            {authState.user !== null &&
            authState.user.isReservoirPortalAdministrator ? (
                <CardContent className={classes.contentMiddle}>
                    <Grid container justify="space-between">
                        <Typography
                            className={classes.title}
                            color="textSecondary"
                            gutterBottom
                            align="left"
                        >
                            {projectHeader.customerName}&nbsp;{projectHeader.id}
                        </Typography>
                        <Typography color="textSecondary" align="right">
                            {enableAddUserFunction ? (
                                <Link
                                    to={`/projects/${projectHeader.id}/users`}
                                    style={{
                                        textDecoration: "none",
                                        margin: "2px"
                                    }}
                                >
                                    <Button
                                        size="small"
                                        color="primary"
                                        style={{
                                            maxWidth: "30px",
                                            maxHeight: "30px",
                                            minWidth: "30px",
                                            minHeight: "30px"
                                        }}
                                        className={classes.button}
                                    >
                                        <PeopleOutlineIcon />
                                    </Button>
                                </Link>
                            ) : (
                                <></>
                            )}
                            {projectHeader.isSyncPermitted ? (
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={handleSync}
                                    disabled={syncing}
                                    style={{
                                        maxWidth: "30px",
                                        maxHeight: "30px",
                                        minWidth: "30px",
                                        minHeight: "30px"
                                    }}
                                    className={classes.button}
                                >
                                    {syncing ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <SyncIcon />
                                    )}
                                </Button>
                            ) : (
                                <></>
                            )}
                        </Typography>
                    </Grid>
                </CardContent>
            ) : (
                <></>
            )}

            <CardContent className={classes.contentBottom}>
                {projectHeader.status !== 0 ? (
                    <Typography component="h2">
                        Status: {statusValues[projectHeader.status]}
                    </Typography>
                ) : (
                    <></>
                )}

                <Typography component="h2">
                    Type: {projectTypes[projectHeader.jobType]}
                </Typography>
                {projectHeader.tracercoKeyContact ? (
                    <Typography component="h2">
                        Key Contact: {projectHeader.tracercoKeyContact}
                    </Typography>
                ) : (
                    <></>
                )}
                {projectHeader.tracercoProjectManager ? (
                    <Typography component="h2">
                        Project Manager: {projectHeader.tracercoProjectManager}
                    </Typography>
                ) : (
                    <></>
                )}
            </CardContent>
            <CardActions>
                <Link
                    to={`/projects/${projectHeader.id}`}
                    style={{
                        textDecoration: "none",
                        margin: "2px",
                        width: "100%"
                    }}
                >
                    <Button
                        fullWidth
                        size="small"
                        color="primary"
                        className={classes.viewProjectButton}
                        style={{ margin: "2px" }}
                    >
                        View Project
                    </Button>
                </Link>
            </CardActions>
        </Card>
    );
};

export default UserProject;
